import { DROFUS_DATE_PRESET_OPTIONS, type DrofusDatePreset } from "./drofusDateRange";
import { DrofusChangesFilterSummary } from "../DrofusChanges/DrofusChangesFilterSummary";
import { DrofusEndringerAttributeFilter } from "./DrofusEndringerAttributeFilter";
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { FiCalendar, FiFilter, FiInfo, FiSearch } from "react-icons/fi";
import { openapi } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { DROFUS_CHANGE_TYPE_OPTIONS } from "../components/DrofusChangeTypeFilter";
import PnsTreePanelContainer from "../DesignTab/routing/PnsTreePanelContainer";
import { clearSelectedPnsIdsInRedux } from "../DesignTab/routing/pnsSelectionSlice";
import {
  DROFUS_ENDRINGER_DISCIPLINE_OPTIONS,
  resetDrofusEndringerFilters,
  setDrofusEndringerDateFrom,
  setDrofusEndringerDatePreset,
  setDrofusEndringerDateTo,
  setDrofusEndringerSearch,
  setDrofusEndringerIncludeSynchronizationChanges,
  setDrofusEndringerViewMode,
  toggleDrofusEndringerChangeType,
  toggleDrofusEndringerDiscipline,
  toggleDrofusEndringerMmi,
  type IDrofusEndringerViewMode,
} from "./drofusEndringerSlice";
import {
  selectDrofusEndringerShowPns,
  selectDrofusEndringerViewMode,
  selectDrofusEndringerState,
} from "./drofusEndringerSelectors";

export const DrofusEndringerFilterBar: React.FC<{ fullWidth?: boolean }> = ({ fullWidth = false }) => {
  const dispatch = useAppDispatch();
  const showPns = useAppSelector(selectDrofusEndringerShowPns);
  const state = useAppSelector(selectDrofusEndringerState);
  const viewMode = useAppSelector(selectDrofusEndringerViewMode);
  const projectNo = useAppSelector((rootState) => rootState.design.projectNo);
  const mmiOptionsArg =
    Number.isFinite(projectNo) && projectNo > 0 ? { body: { projectNo } } : skipToken;
  const { data: mmiOptions } =
    openapi.usePostMultiplacerDrofusInterfaceProjectChangeEventMmiOptionsQuery(mmiOptionsArg);

  return (
    <Paper
      withBorder
      radius="sm"
      p="sm"
      w={fullWidth ? "100%" : 300}
      maw="100%"
      style={{ flexShrink: 0, maxHeight: "100%", overflowY: "auto" }}
    >
      <Stack gap="md">
        <Group gap="xs">
          <FiFilter size={16} />
          <Text fw={700}>Filter</Text>
        </Group>

        {!showPns && <DrofusChangesFilterSummary />}

        {showPns && (
          <Stack gap={6}>
            <Text size="xs" fw={700} c="dimmed">
              VISNING
            </Text>
            <SegmentedControl
              size="xs"
              color="blue"
              fullWidth
              value={viewMode}
              onChange={(value) =>
                dispatch(setDrofusEndringerViewMode(value as IDrofusEndringerViewMode))
              }
              data={[
                { value: "changes", label: "Alle endringer" },
                { value: "rooms", label: "Gruppert per rom" },
              ]}
              styles={{
                root: {
                  border: "1px solid var(--mantine-color-blue-3)",
                  backgroundColor: "var(--mantine-color-blue-0)",
                },
                label: {
                  fontWeight: 700,
                },
              }}
            />
          </Stack>
        )}

        <Switch
          size="sm"
          label="Vis synkroniseringsendringer"
          description="IFC GUID, RefString, Nummer, Utvidet TFM-ID og synkroniserings-ID"
          checked={state.includeSynchronizationChanges ?? false}
          onChange={(event) =>
            dispatch(setDrofusEndringerIncludeSynchronizationChanges(event.currentTarget.checked))
          }
        />

        <DrofusEndringerAttributeFilter />

        <TextInput
          label="Søk"
          placeholder="Rom, artikkel, felt..."
          leftSection={<FiSearch size={14} />}
          value={state.search}
          onChange={(event) => dispatch(setDrofusEndringerSearch(event.currentTarget.value))}
        />

        {showPns && (
          <Stack gap={6}>
            <Text size="xs" fw={700} c="dimmed">
              PNS
            </Text>
            <Box h={320} style={{ minHeight: 0 }}>
              <PnsTreePanelContainer />
            </Box>
          </Stack>
        )}

        <Stack gap="xs">
          <Group gap="xs">
            <FiCalendar size={16} />
            <Text fw={600} size="sm">
              Dato
            </Text>
          </Group>
          <Select
            label="Periode"
            data={[...DROFUS_DATE_PRESET_OPTIONS]}
            value={state.datePreset}
            allowDeselect={false}
            onChange={(value) => {
              if (value) dispatch(setDrofusEndringerDatePreset(value as DrofusDatePreset));
            }}
          />
          <Group grow align="flex-start">
            <TextInput
              type="date"
              label="Fra"
              value={state.dateFrom}
              onChange={(event) => dispatch(setDrofusEndringerDateFrom(event.currentTarget.value))}
            />
            <TextInput
              type="date"
              label="Til"
              value={state.dateTo}
              onChange={(event) => dispatch(setDrofusEndringerDateTo(event.currentTarget.value))}
            />
          </Group>
        </Stack>

        <FilterCheckboxGroup label="Type">
          {DROFUS_CHANGE_TYPE_OPTIONS.map((option) => (
            <Checkbox
              key={option.value}
              label={option.label}
              size="sm"
              color={option.inactiveColor}
              checked={state.changeTypes.includes(option.value)}
              onChange={() => dispatch(toggleDrofusEndringerChangeType(option.value))}
              styles={checkboxStyles}
            />
          ))}
        </FilterCheckboxGroup>

        <FilterCheckboxGroup label="Fag">
          {DROFUS_ENDRINGER_DISCIPLINE_OPTIONS.map((option) => (
            <Checkbox
              key={option.value}
              label={option.label}
              size="sm"
              color={option.color}
              checked={state.affectedDisciplines.includes(option.value)}
              onChange={() => dispatch(toggleDrofusEndringerDiscipline(option.value))}
              styles={checkboxStyles}
            />
          ))}
        </FilterCheckboxGroup>

        {(mmiOptions?.items.length ?? 0) > 0 && (
          <FilterCheckboxGroup
            label="MMI"
            tooltip="Filtrerer på forekomstens MMI i den lagrede innlesningen som hører til endringen, ikke dagens verdi i dRofus. Når selve MMI-verdien endres, treffes også gammel og ny verdi. En endring fra 350 til 325 vises derfor ved valg av både 350 og 325."
          >
            {mmiOptions?.items.map((mmiCode) => (
              <Checkbox
                key={mmiCode}
                label={mmiCode}
                size="sm"
                checked={state.mmiCodes.includes(mmiCode)}
                onChange={() => dispatch(toggleDrofusEndringerMmi(mmiCode))}
                styles={checkboxStyles}
              />
            ))}
          </FilterCheckboxGroup>
        )}

        <Button
          variant="light"
          onClick={() => {
            dispatch(resetDrofusEndringerFilters());
            if (showPns) dispatch(clearSelectedPnsIdsInRedux());
          }}
        >
          Tilbakestill filter
        </Button>
      </Stack>
    </Paper>
  );
};

const checkboxStyles = {
  input: { cursor: "pointer" },
  label: { cursor: "pointer", paddingLeft: 7 },
};

const FilterCheckboxGroup: React.FC<
  React.PropsWithChildren<{ label: string; tooltip?: string }>
> = ({ label, tooltip, children }) => (
  <Box component="fieldset" m={0} p={0} style={{ border: 0, minWidth: 0 }}>
    <Text
      component="legend"
      size="xs"
      fw={700}
      c="dimmed"
      p={0}
      style={{ display: "flex", alignItems: "center", gap: 4 }}
    >
      {label.toUpperCase()}
      {tooltip && (
        <Tooltip
          label={tooltip}
          multiline
          w={300}
          maw="100%"
          withArrow
          events={{ hover: true, focus: true, touch: true }}
        >
          <ActionIcon
            type="button"
            variant="subtle"
            color="gray"
            size="xs"
            aria-label={`Om ${label}-filteret`}
          >
            <FiInfo size={14} />
          </ActionIcon>
        </Tooltip>
      )}
    </Text>
    <Text size="xs" c="dimmed" mt={2} mb="xs">
      Ingen valgt = alle
    </Text>
    <Group gap="sm">{children}</Group>
  </Box>
);
